import React, { useRef, useEffect, useState } from 'react';
import { Briefcase, Calendar } from 'lucide-react';

function TimelineItem({ item, index, isMobile }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 0.15}s` }}
    >
      {/* Timeline dot */}
      <div
        className={`absolute ${
          isMobile
            ? '-left-[9px] top-1'
            : index % 2 === 0
              ? '-right-[9px] top-1'
              : '-left-[9px] top-1'
        } w-4 h-4 rounded-full border-2 border-dark-950 z-10`}
        style={{ backgroundColor: item.color }}
      >
        <div
          className="absolute inset-1 rounded-full animate-pulse"
          style={{ backgroundColor: item.color, opacity: 0.4 }}
        />
      </div>

      {/* Content card */}
      <div
        className={`p-5 lg:p-6 rounded-2xl glass border border-white/[0.04] hover:border-white/[0.08] transition-all duration-500 hover:translate-y-[-2px] ${
          isMobile
            ? 'ml-6'
            : index % 2 === 0
              ? 'mr-8 text-right'
              : 'ml-8'
        }`}
        style={{
          boxShadow: isVisible
            ? `0 0 30px ${item.color}08`
            : 'none',
        }}
      >
        {/* Date */}
        <div className={`flex items-center gap-2 mb-3 ${isMobile ? '' : index % 2 === 0 ? 'justify-end' : ''}`}>
          <Calendar className="w-3 h-3 text-gray-500" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
            {item.year}
          </span>
        </div>

        {/* Company */}
        <h3 className="font-display font-bold text-lg lg:text-xl text-white mb-1">
          {item.company}
        </h3>

        {/* Role */}
        <div className="flex items-center gap-2 mb-3" style={{ justifyContent: isMobile ? 'flex-start' : index % 2 === 0 ? 'flex-end' : 'flex-start' }}>
          <Briefcase className="w-3 h-3" style={{ color: item.color }} />
          <span className="text-xs font-medium" style={{ color: item.color }}>
            {item.role}
          </span>
        </div>

        {/* Description */}
        <p className={`text-sm text-gray-500 leading-relaxed ${index % 2 === 0 && !isMobile ? 'ml-auto' : ''}`}>
          {item.description}
        </p>
      </div>
    </div>
  );
}

export default function Experience({ experience, visible, isMobile }) {
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => setTitleVisible(true), 100);
    } else {
      setTitleVisible(false);
    }
  }, [visible]);

  return (
    <section className="relative min-h-screen flex items-center px-6 lg:px-12 py-24 lg:py-32">
      <div className="max-w-5xl mx-auto w-full">
        {/* Section header */}
        <div
          className={`text-center mb-16 lg:mb-20 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-4 justify-center">
            <div className="w-8 h-[2px] bg-gradient-to-r from-accent-purple to-accent-blue rounded-full" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">
              Trayectoria
            </span>
            <div className="w-8 h-[2px] bg-gradient-to-r from-accent-blue to-accent-purple rounded-full" />
          </div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-white mb-4">
            Mi <span className="text-gradient">Experiencia</span>
          </h2>
          <p className="text-sm lg:text-base text-gray-500">
            Julio 2018 — Noviembre 2025
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Center line (desktop only) */}
          {!isMobile && (
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue/30 via-accent-purple/30 to-accent-cyan/30" />
          )}

          {/* Mobile line */}
          {isMobile && (
            <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue/30 via-accent-purple/30 to-accent-cyan/30" />
          )}

          <div className={`space-y-10 lg:space-y-16 ${isMobile ? 'ml-2' : ''}`}>
            {experience.map((item, index) => (
              <div
                key={index}
                className={`relative ${isMobile ? '' : index % 2 === 0 ? 'pr-[calc(50%+2rem)]' : 'pl-[calc(50%+2rem)]'}`}
              >
                <TimelineItem item={item} index={index} isMobile={isMobile} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
